import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { AppBarWithButtons } from '../../../../components/designe/app-bars/app-bar-with-action-buttons/AppBarWithButtons';
import AppDialog from '../../../../components/reusable/app-dialog/AppDialog';
import SetGroupEditTable from '../../../../components/reusable/set-group-edit-table/SetGroupEditTable';
import { useUnsavedChangesGuard } from '../../../../hooks/useUnsavedChangesGuard';
import { ITrainingSet } from '../../../../types/interfaces/training-set';
import { useStyles } from './EditTrainingSetModal.styles';
import useEditTrainingSetTable from './useEditTrainingSetTable';

interface IEditTrainingSetModalProps {
  setTrainingSetGroup: (value: ITrainingSet[]) => void;
  trainingSetGroup?: ITrainingSet[];
  isSavingTrainingSets: boolean;
  onSubmit: (value: ITrainingSet[]) => void;
  trainingOwnerId?: string;
  showSetItemStatus?: boolean;
  trainingOptionValueField?: 'programValue' | 'value';
  trainingOptionValueField2?: 'programValue2' | 'value2';
  copyLastTrainingSetItemStatusOnAdd?: boolean;
}

export function EditTrainingSetModal({
  setTrainingSetGroup,
  trainingSetGroup,
  isSavingTrainingSets,
  onSubmit,
  trainingOwnerId,
  showSetItemStatus = false,
  trainingOptionValueField = 'programValue',
  trainingOptionValueField2 = 'programValue2',
  copyLastTrainingSetItemStatusOnAdd = false,
}: IEditTrainingSetModalProps) {
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const { t } = useTranslation();

  const [localSets, setLocalSets] = useState<ITrainingSet[]>(null);

  useEffect(() => {
    if (trainingSetGroup) {
      setLocalSets([...trainingSetGroup.map((set) => ({ ...set }))]);
    } else {
      setLocalSets(null);
    }
  }, [trainingSetGroup]);

  function onCancel() {
    setLocalSets(null);
    setTrainingSetGroup(null);
  }

  const useSetGroupEditTableBase = useEditTrainingSetTable(setLocalSets, {
    trainingOwnerId,
    showSetItemStatus,
    trainingOptionValueField,
    trainingOptionValueField2,
    copyLastTrainingSetItemStatusOnAdd,
  });

  const sets = useMemo(() => localSets, [localSets]);
  const isDirty = useMemo(
    () =>
      JSON.stringify(localSets || []) !==
      JSON.stringify(trainingSetGroup || []),
    [localSets, trainingSetGroup],
  );
  const { allowAndLeave, runLeave, dialog } = useUnsavedChangesGuard({
    isDirty,
    leaveAction: onCancel,
  });

  const useSetGroupEditTable = useMemo(
    () => ({
      ...useSetGroupEditTableBase,
      options: {
        ...useSetGroupEditTableBase.options,
        hideHeader: !localSets || localSets.length <= 1,
      },
    }),
    [useSetGroupEditTableBase, localSets],
  );

  return (
    <>
      {dialog}
      <AppDialog
        classes={{ root: classes.newTrainingSetDialog }}
        open={!!localSets}
        onClose={() => runLeave(onCancel)}
        title={
          trainingSetGroup?.find((set) => set.id)
            ? t('trainings.editTrainingSet')
            : t('trainings.newTrainingSet')
        }>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(localSets.filter((set) => !!set.exerciseId));
          }}>
          {!!localSets && (
            <Box className={classes.setsContainer}>
              <SetGroupEditTable
                useSetGroupEditTable={useSetGroupEditTable}
                sets={sets}
              />
            </Box>
          )}
          <Box className={classes.buttonWrapper}>
            <AppBarWithButtons
              onCancelClick={allowAndLeave}
              loading={isSavingTrainingSets}
              disabled={!sets?.find((set) => !!set.exerciseId)}
            />
          </Box>
        </form>
      </AppDialog>
    </>
  );
}
